<template>
    <div class="list">
        <div class="header">
            <div class="operation-name">
                <div @click="setSortBy('operationName')">
                    Operation Name
                    <span
                        v-if="sortBy === 'operationName'"
                        class="mdi"
                        :class="{ 'mdi-arrow-up': sortDir === 'asc', 'mdi-arrow-down': sortDir === 'desc' }"
                    />
                </div>
                <div @click="setSortBy('fileClassification')">
                    Category
                    <span
                        v-if="sortBy === 'fileClassification'"
                        class="mdi"
                        :class="{ 'mdi-arrow-up': sortDir === 'asc', 'mdi-arrow-down': sortDir === 'desc' }"
                    />
                </div>
            </div>
            <div
                v-if="showSiteStatus"
                class="site-status"
                @click="setSortBy('currentSiteStatus')"
            >
                Site Status
                <span
                    v-if="sortBy === 'currentSiteStatus'"
                    class="mdi"
                    :class="{ 'mdi-arrow-up': sortDir === 'asc', 'mdi-arrow-down': sortDir === 'desc' }"
                />
            </div>
            <div class="atsPbl" />
            <div class="docsCount" />
            <div class="detail-btn" />
        </div>
        <div
            ref="recordsWrapperRef"
            class="records-wrapper"
        >
            <div
                v-if="isInit && listData.length == 0"
                class="norecords"
            >
                No records returned
            </div>
            <div
                v-if="!isInit"
                class="records"
            >
                <ListItem
                    v-for="i in shadowLength"
                    :id="`skeleton-item-${i}`"
                    :key="i"
                    :is-skeleton="true"
                />
            </div>
            <div
                v-else
                ref="recordsContainerRef"
                class="records"
            >
                <ListItem
                    v-for="item in listData"
                    :id="`item-${item.id}`"
                    :key="item.id"
                    :item="item"
                    :is-active="state.openSite && state.openSite.id == item.id"
                    :show-site-status="showSiteStatus"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, onBeforeUnmount, nextTick, ref, watch, onUpdated } from 'vue';
import _ from 'lodash';

import useApp from '@/composables';
import ListItem from './ListItem.vue';

type SortBy = 'operationName' | 'fileClassification' | 'currentSiteStatus';
type SortDir = 'asc' | 'desc';

export default defineComponent({
    name: 'List',
    components: { ListItem },
    setup() {
        const recordsPerPage = 25;
        const shadowLength = 8;

        const pageNumber = ref<number>(1);
        const recordsWrapperRef = ref<HTMLElement>();
        const recordsContainerRef = ref<HTMLElement>();
        const sortBy = ref<SortBy>('fileClassification');
        const sortDir = ref<SortDir>('desc');
        const scrollToItem = ref<boolean>(false);

        const { state } = useApp();

        const isInit = computed(() => state.isInit);
        const maxPages = computed(() => Math.ceil(state.listViewSites.length / recordsPerPage));

        const sortedData = computed(() => {
            let newList = _([...state.listViewSites])
                .sortBy((r) => {
                    switch (sortBy.value) {
                    case 'operationName':
                    case 'fileClassification':
                        return r[sortBy.value];

                    default:
                    case 'currentSiteStatus':
                        switch (r.currentSiteStatus) {
                        case 'OPN': return 2;
                        case 'PND': return 1;
                        default: return 0;
                        }
                    }
                })
                .value();

            if (sortDir.value === 'desc') {
                newList = newList.reverse();
            }

            return newList;
        });

        const listData = computed(() => {
            const start = 0;
            const end = pageNumber.value * recordsPerPage;
            return sortedData.value.slice(start, end);
        });

        const showSiteStatus = computed(() => sortedData.value.some((r) => r.currentSiteStatusDescription !== 'Unavailable'));

        const toggleSortDir = (): void => {
            if (sortDir.value === 'asc') {
                sortDir.value = 'desc';
            } else {
                sortDir.value = 'asc';
            }
        };

        const setSortBy = (by: SortBy): void => {
            if (sortBy.value === by) {
                toggleSortDir();
            } else {
                sortBy.value = by;
            }
        };

        const buildDocsUrl = (siteId: number) : string => {
            let s = siteId.toString();
            while (s.length < 8) {
                s = `0${s}`;
            }
            return `http://esar.alberta.ca/esarmain.aspx?esaid=${s}`;
        };

        const onScroll = () => {
            const buffer = 100;

            const wrapperBoxHeight = recordsWrapperRef.value?.getBoundingClientRect().height ?? -1;
            const wrapperScrollTop = recordsWrapperRef.value?.scrollTop ?? -1;
            const recordsBoxHeight = recordsContainerRef.value?.getBoundingClientRect().height ?? -1;
            const pos = recordsBoxHeight - wrapperScrollTop;

            if (pos < wrapperBoxHeight + buffer && pageNumber.value < maxPages.value) {
                pageNumber.value += 1;
            }
        };

        watch(() => state.isSearching, (newVal, oldVal) => {
            nextTick(() => {
                // was searching
                if (recordsWrapperRef.value && newVal === false && oldVal === true) {
                    recordsWrapperRef.value.scrollTop = 0;
                }
            });
        });

        watch([() => state.listViewSites, sortDir, sortBy], () => {
            pageNumber.value = 1;
        });

        watch(() => state.openSite, () => {
            if (!state.openSite) {
                return;
            }

            const index = sortedData.value.findIndex((i) => i.id === state.openSite?.id);
            if (index === -1) {
                return;
            }

            const page = Math.floor(index / recordsPerPage) + 1;
            pageNumber.value = Math.max(pageNumber.value, page);
            scrollToItem.value = true;
        });

        onUpdated(() => {
            nextTick(() => {
                if (scrollToItem.value === false || !state.openSite?.id) {
                    return;
                }

                const el = recordsContainerRef.value?.querySelector(`#item-${state.openSite?.id}`);
                if (recordsWrapperRef.value && el) {
                    // scroll just the records wrapper to the correct offset position.
                    // this should work as long as the records div has position: relative;
                    recordsWrapperRef.value.scroll({
                        top: (el as HTMLElement).offsetTop,
                        behavior: 'smooth'
                    });
                    scrollToItem.value = false;
                }
            });
        });

        onMounted(() => {
            nextTick(() => {
                recordsWrapperRef.value?.addEventListener('scroll', onScroll);
            });
        });

        onBeforeUnmount(() => {
            recordsWrapperRef.value?.removeEventListener('scroll', onScroll);
        });

        return {
            state,
            listData,
            recordsWrapperRef,
            recordsContainerRef,
            sortBy,
            sortDir,
            isInit,
            shadowLength,
            buildDocsUrl,
            setSortBy,
            showSiteStatus,
        };
    }
});
</script>

<style lang="postcss" scoped>
.list {
    @apply flex flex-col items-stretch overflow-hidden h-full font-helvetica;

    .header {
        @apply flex flex-row border-gray-200 px-2;
        border-bottom-width: 1px;

        .operation-name,
        .site-status,
        .atsPbl,
        .docsCount,
        .detail-btn{
            @apply flex items-center font-semibold text-base cursor-pointer px-3 py-2;

            div:not(.mdi) {
                @apply inline-block flex-1;
            }
        }
    }

    .operation-name {
        width: 46%;
    }

    .site-status {
        width: 20%;
    }

    .atsPbl {
        width: 20%;
    }

    .docsCount {
        width: 7%;
    }

    .detail-btn {
        width: 7%;
    }

    .records-wrapper {
        @apply overflow-y-scroll;

        .records {
            @apply relative flex flex-col;
        }

        .norecords {
            @apply mt-5 text-lg pl-5;
        }
    }
}
</style>
